import React, { useState, useEffect, Fragment, useRef, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FaSearch, FaCheck, FaTimes, FaFilter, FaSpinner, FaAngleDown, FaArrowLeft, FaBoxOpen, FaBox, FaListUl } from 'react-icons/fa';
import { fetchProducts, fetchProductsUsingFilter } from '../controllers/products-controller';
import StockController from '../controllers/stock-controller';

const ProductSelectionModal = ({ 
  isOpen, 
  onClose, 
  onProductSelect, 
  allowOutOfStock = false, // Add this prop with a default value of false
  purchaseMode = false // When true, use purchase price (prixAchat) instead of selling price
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Tous');
  const [categories, setCategories] = useState(['Tous']);
  
  // Stock filter state
  const [stockFilter, setStockFilter] = useState('all'); // 'all', 'inStock', 'outOfStock'
  
  // For variant selection UI
  const [selectedProductForVariants, setSelectedProductForVariants] = useState(null);
  
  // Variant search state
  const [variantSearchTerm, setVariantSearchTerm] = useState('');
  
  // For mobile view
  const [showCartOnMobile, setShowCartOnMobile] = useState(false);
  
  // Force re-fetch state
  const [filterApplied, setFilterApplied] = useState(false);

  // Per-product / per-variant stock breakdown loaded from the Stock table
  //   productStock[productId]      = { total, perDepot: [{ depotId, depotName, qty }] }
  //   variantStock[`${pid}:${vid}`] = { total, perDepot: [{ depotId, depotName, qty }] }
  const [productStock, setProductStock] = useState({});
  const [variantStock, setVariantStock] = useState({});
  
  const observer = useRef();
  const lastProductElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);
  
  // Load products when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    setProducts([]);
    setPage(1);
    setHasMore(true);
  }, [isOpen]);
  
  // Fetch products when page changes or filters are applied
  useEffect(() => {
    if (!isOpen) return;
    
    const loadProducts = async () => {
      try {
        setLoading(true);
        
        // Build filter params
        const filterParams = {
          page: page,
          pageSize: 10,
          search: searchTerm
        };
        
        // Apply stock filter
        if (stockFilter === 'inStock') {
          filterParams.inStock = true;
        } else if (stockFilter === 'outOfStock') {
          filterParams.inStock = false;
        }
        
        // If a specific category is selected (not 'Tous'), apply the filter
        if (activeCategory !== 'Tous') {
          filterParams.brand = activeCategory;
        }
        
        let response;
        
        // Use different fetch functions based on whether we're searching or not
        if (searchTerm || stockFilter !== 'all') {
          // If there's a search term or stock filter, use fetchProductsUsingFilter
          console.log('Searching products with filters:', filterParams);
          response = await fetchProductsUsingFilter(filterParams);
        } else {
          // Otherwise use the regular fetchProducts
          response = await fetchProducts(page, 10);
        }
        
        console.log('Products response:', response);
        
        // Check if we have products
        if (response && response.data && response.data.list) {
          const productsList = response.data.list;
          console.log('Found products:', productsList.length);
          
          setTotalProducts(response.totalProduct || 0);
          
          // Append new products to existing ones if pagination
          if (page === 1) {
            setProducts(productsList);
          } else {
            setProducts(prevProducts => [...prevProducts, ...productsList]);
          }
          
          // Check if we have more products to load
          const totalItems = response.data.total || 0;
          setHasMore(productsList.length > 0 && (page * 10) < totalItems);
          
        } else {
          console.warn('No products found or invalid response format');
          setProducts([]);
          setTotalProducts(0);
          setHasMore(false);
        }
      } catch (error) {
        console.error('Error loading products:', error);
        setProducts([]);
        setTotalProducts(0);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };
    
    loadProducts();
  }, [isOpen, page, filterApplied]);

  // Whenever the visible product list changes, fetch real stock per depot from the Stock table.
  useEffect(() => {
    if (!isOpen) return;
    const ids = products.map((p) => p.id).filter(Boolean);
    if (ids.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const lines = await StockController.getByProducts(ids);
        if (cancelled || !Array.isArray(lines)) return;
        const prodMap = {};
        const varMap = {};
        lines.forEach((l) => {
          const entry = { depotId: l.depotId, depotName: l.depotName || '—', qty: l.quantite || 0 };
          if (l.productVariantId) {
            const k = `${l.productId}:${l.productVariantId}`;
            if (!varMap[k]) varMap[k] = { total: 0, perDepot: [] };
            varMap[k].perDepot.push(entry);
            varMap[k].total += entry.qty;
          } else {
            if (!prodMap[l.productId]) prodMap[l.productId] = { total: 0, perDepot: [] };
            prodMap[l.productId].perDepot.push(entry);
            prodMap[l.productId].total += entry.qty;
          }
        });
        // For variant products, also build product-level total = sum of all its variants
        ids.forEach((pid) => {
          const variantEntries = Object.entries(varMap).filter(([k]) => k.startsWith(`${pid}:`));
          if (variantEntries.length > 0) {
            const perDepotAgg = {};
            let total = 0;
            variantEntries.forEach(([, v]) => {
              total += v.total;
              v.perDepot.forEach((d) => {
                if (!perDepotAgg[d.depotId]) perDepotAgg[d.depotId] = { depotId: d.depotId, depotName: d.depotName, qty: 0 };
                perDepotAgg[d.depotId].qty += d.qty;
              });
            });
            prodMap[pid] = { total, perDepot: Object.values(perDepotAgg) };
          }
        });
        setProductStock((prev) => ({ ...prev, ...prodMap }));
        setVariantStock((prev) => ({ ...prev, ...varMap }));
      } catch (e) {
        console.error('Failed to load stock breakdown', e);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, products]);
  
  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedProducts([]);
      setSelectedProductForVariants(null);
      setShowCartOnMobile(false);
    }
  }, [isOpen]);
  
  // Resolve the price to use depending on mode (selling vs purchase).
  const getUnitPrice = (product, variant = null) => {
    if (purchaseMode) {
      return (
        variant?.prixAchatTTC ?? variant?.PrixAchatTTC ??
        product?.prixAchatTTC ?? product?.PrixAchatTTC ?? 0
      );
    }
    return product?.prixVenteTTC ?? product?.PrixVenteTTC ?? 0;
  };

  // Handle product click - show variants or toggle selection
  const handleProductClick = (product) => {
    if (product.isVariant && product.productVariants && product.productVariants.length > 0) {
      // Show variants selection
      setSelectedProductForVariants(product);
    } else {
      // Toggle selection for non-variant products - allow out of stock if allowOutOfStock is true
      if (allowOutOfStock || product.stock > 0) {
        toggleRegularProductSelection(product);
      }
    }
  };
  
  // Toggle selection for a regular product (no variants)
  const toggleRegularProductSelection = (product) => {
    const isSelected = selectedProducts.some(p => p.id === product.id);
    
    if (isSelected) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else {
      console.log('Adding product to selection:', product);
      setSelectedProducts([...selectedProducts, { 
        id: product.id,
        productId: product.id,
        name: product.name,
        reference: product.reference || 'Sans référence',
        // Keep both displayed price and raw promo metadata so callers can choose promo-aware price
        price: getUnitPrice(product),
        prixVentePromo: product.prixVentePromo ?? product.PrixVentePromo ?? 0,
        enPromo: product.enPromo ?? product.EnPromo ?? false,
        // include original product object for downstream consumers that need more fields
        product: product,
        imageUrl: product.imageUrl && product.imageUrl.length ? product.imageUrl[0] : '/placeholder.jpg',
        quantity: 1,
        stock: product.stock,
        isVariant: false
      }]);
    }
  };
  
  // Toggle selection for a product variant
  const toggleVariantSelection = (product, variant) => {
    // Allow selection regardless of stock if allowOutOfStock is true
    const variantId = variant.id;
    const isSelected = selectedProducts.some(p => p.id === variantId);
    
    if (isSelected) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== variantId));
    } else {
      setSelectedProducts([...selectedProducts, { 
        id: variantId,
        productId: product.id,
        variantId: variantId,
        name: `${product.name} - ${variant.name}`,
        reference: variant.reference || product.reference || 'Sans référence',
        // Store product-level price as well as promo metadata and references
        price: getUnitPrice(product, variant),
        prixVentePromo: product.prixVentePromo ?? product.PrixVentePromo ?? variant.prixVentePromo ?? 0,
        enPromo: product.enPromo ?? product.EnPromo ?? variant.enPromo ?? false,
        product: product,
        variant: variant,
        quantity: 1,
        stock: variant.stock,
        isVariant: true,
        variant: variant
      }]);
    }
  };
  
  // Update quantity for selected product
  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) return;
    
    setSelectedProducts(selectedProducts.map(product => {
      if (product.id === productId) {
        // If allowOutOfStock is true, don't restrict by stock amount
        if (allowOutOfStock) {
          return { ...product, quantity };
        } else {
          // Otherwise limit by stock as before
          return { ...product, quantity: Math.min(product.stock, quantity) };
        }
      }
      return product;
    }));
  };
  
  // Remove a product from selection
  const removeFromSelection = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };
  
  // Handle the final selection
  const handleConfirmSelection = () => {
    if (selectedProducts.length > 0) {
      onProductSelect(selectedProducts);
      onClose();
    }
  };
  
  // Toggle mobile cart view
  const toggleMobileCart = () => {
    setShowCartOnMobile(!showCartOnMobile);
  };
  
  // Apply filters
  const handleApplyFilters = () => {
    console.log('Applying filters with search term:', searchTerm);
    // Reset to page 1 when applying filters
    setPage(1);
    // Toggle filterApplied to trigger a reload
    setFilterApplied(prev => !prev);
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStockFilter('all');
    setPage(1);
    setFilterApplied(prev => !prev);
  };
  
  // Render the main product selection view
  const renderProductList = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {products.length > 0 ? (
        products.map((product, index) => {
          // Check if this is the last element for infinite scroll
          const isLastElement = index === products.length - 1;
          
          // Check if this product or any of its variants is selected
          const isSelected = product.isVariant
            ? selectedProducts.some(p => p.productId === product.id)
            : selectedProducts.some(p => p.id === product.id);
            
          // Check if the product is out of stock (using real Stock table data)
          const stockEntry = productStock[product.id];
          const realStockTotal = stockEntry ? stockEntry.total : (product.stock || 0);
          const isOutOfStock = realStockTotal <= 0;

          // For variant products, check if they have any in-stock variants
          const hasInStockVariants = product.isVariant &&
            product.productVariants &&
            product.productVariants.some(v => {
              const vs = variantStock[`${product.id}:${v.id}`];
              return (vs ? vs.total : v.stock) > 0;
            });
            
          // Determine if this product can be selected
          // If allowOutOfStock is true, we always allow selection
          const canBeSelected = allowOutOfStock ? true : (product.isVariant ? hasInStockVariants : !isOutOfStock);
          
          return (
            <div
              key={product.id}
              ref={isLastElement ? lastProductElementRef : null}
              className={`border rounded-lg p-4 ${
                !canBeSelected && !allowOutOfStock
                  ? 'border-gray-200 bg-gray-50 opacity-70' 
                  : isSelected
                    ? 'border-[#174540] bg-[#f5f8f7] shadow-md cursor-pointer'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow cursor-pointer'
              }`}
              onClick={() => {
                // Allow clicking based on canBeSelected or allowOutOfStock
                if (canBeSelected) {
                  handleProductClick(product);
                }
              }}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">{product.name}</h4>
                  <p className="mt-1 text-xs text-gray-500">{product.reference || 'Sans référence'}</p>
                  <div className="mt-2 flex items-center flex-wrap gap-2">
                    <span className="text-sm font-medium text-gray-900">{getUnitPrice(product)?.toFixed(2)} TND</span>
                    {(() => {
                      const st = productStock[product.id];
                      const total = st ? st.total : 0;
                      return (
                        <>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            total > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            Stock: {total}
                          </span>
                          {st && st.perDepot.length > 0 && st.perDepot.map((d) => (
                            <span key={d.depotId} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700">
                              {d.depotName}: {d.qty}
                            </span>
                          ))}
                        </>
                      );
                    })()}
                    {product.isVariant && product.productVariants && product.productVariants.length > 0 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {product.productVariants.length} variante{product.productVariants.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ml-4">
                  {product.isVariant && product.productVariants && product.productVariants.length > 0 ? (
                    <button 
                      className={`h-7 w-7 rounded-full flex items-center justify-center ${
                        allowOutOfStock || hasInStockVariants ? 'bg-gray-100 hover:bg-gray-200' : 'bg-gray-100 cursor-not-allowed'
                      }`}
                      disabled={!allowOutOfStock && !hasInStockVariants}
                    >
                      <FaAngleDown className="h-3 w-3 text-gray-600" />
                    </button>
                  ) : (
                    isOutOfStock && !allowOutOfStock ? (
                      <span className="text-xs text-red-500">Rupture</span>
                    ) : isSelected ? (
                      <div className="h-6 w-6 rounded-full bg-[#174540] flex items-center justify-center">
                        <FaCheck className="h-3 w-3 text-white" />
                      </div>
                    ) : (
                      <div className="h-6 w-6 rounded-full border-2 border-gray-300"></div>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="col-span-2 flex flex-col items-center justify-center py-8">
          <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h4 className="mt-2 text-gray-500 text-lg font-medium">Aucun produit trouvé</h4>
          <p className="mt-1 text-gray-400 text-sm">Essayez de modifier votre recherche ou vos filtres</p>
        </div>
      )}
      {loading && page > 1 && (
        <div className="flex justify-center p-4 col-span-2">
          <FaSpinner className="h-6 w-6 text-[#174540] animate-spin" />
        </div>
      )}
    </div>
  );
  
  // Render the variant selection view
  const renderVariantSelectionView = () => {
    const product = selectedProductForVariants;
    if (!product) return null;
    
    const variants = product.productVariants || [];
    
    // Filter variants based on search term
    const filteredVariants = variants.filter(variant => {
      // Apply search filter (front-end only)
      const matchesSearch = !variantSearchTerm || 
        variant.name.toLowerCase().includes(variantSearchTerm.toLowerCase()) ||
        (variant.reference && variant.reference.toLowerCase().includes(variantSearchTerm.toLowerCase()));
      
      // Apply stock filter using real Stock-table totals when available
      const vs = variantStock[`${product.id}:${variant.id}`];
      const realQty = vs ? vs.total : (variant.stock || 0);
      const matchesStock = stockFilter === 'all' || 
        (stockFilter === 'inStock' && realQty > 0) || 
        (stockFilter === 'outOfStock' && realQty <= 0);
        
      return matchesSearch && matchesStock;
    });
    
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center">
          <button 
            className="mr-3 p-1.5 rounded-full hover:bg-gray-100"
            onClick={() => {
              setSelectedProductForVariants(null);
              setVariantSearchTerm(''); // Clear variant search when going back
            }}
          >
            <FaArrowLeft className="h-4 w-4 text-gray-600" />
          </button>
          <div>
            <h4 className="text-sm font-medium text-gray-900">Sélectionner une variante</h4>
            <p className="text-xs text-gray-500">{product.name}</p>
          </div>
        </div>
        
        {/* Add variant search */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="pl-10 py-2 block w-full shadow-sm border-gray-300 rounded-md focus:ring-[#174540] focus:border-[#174540]"
              placeholder="Rechercher une variante..."
              value={variantSearchTerm}
              onChange={(e) => setVariantSearchTerm(e.target.value)}
            />
            {variantSearchTerm && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  onClick={() => setVariantSearchTerm('')}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  type="button"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {filteredVariants.length > 0 ? (
            <div className="space-y-3">
              {filteredVariants.map(variant => {
                const isSelected = selectedProducts.some(p => p.id === variant.id);
                const vstock = variantStock[`${product.id}:${variant.id}`];
                const vTotal = vstock ? vstock.total : (variant.stock || 0);
                const isOutOfStock = vTotal <= 0;
                
                return (
                  <div 
                    key={variant.id}
                    className={`p-4 border rounded-lg ${
                      isOutOfStock && !allowOutOfStock
                        ? 'border-gray-200 bg-gray-50 opacity-70' 
                        : isSelected 
                          ? 'border-[#174540] bg-[#f5f8f7] cursor-pointer' 
                          : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                    }`}
                    onClick={() => {
                      // Allow selection if allowOutOfStock or if not out of stock
                      if (allowOutOfStock || !isOutOfStock) {
                        toggleVariantSelection(product, variant);
                      }
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h5 className="text-sm font-medium text-gray-900">{variant.name}</h5>
                        <p className="mt-1 text-xs text-gray-500">{variant.reference || 'Sans référence'}</p>
                        <div className="mt-2 flex items-center flex-wrap gap-2">
                          <span className="text-sm font-medium text-gray-900">{getUnitPrice(product, variant)?.toFixed(2)} TND</span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            vTotal > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            Stock: {vTotal}
                          </span>
                          {vstock && vstock.perDepot.map((d) => (
                            <span key={d.depotId} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700">
                              {d.depotName}: {d.qty}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        {isOutOfStock && !allowOutOfStock ? (
                          <span className="text-xs text-red-500">Rupture</span>
                        ) : isSelected ? (
                          <div className="h-6 w-6 rounded-full bg-[#174540] flex items-center justify-center">
                            <FaCheck className="h-3 w-3 text-white" />
                          </div>
                        ) : (
                          <div className="h-6 w-6 rounded-full border-2 border-gray-300"></div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="mt-2 text-gray-500 text-lg font-medium">Aucune variante trouvée</h4>
              <p className="mt-1 text-gray-400 text-sm">
                {variantSearchTerm ? 'Aucun résultat pour votre recherche' : 'Ce produit n\'a pas de variantes'}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Render the selected products view for mobile and desktop
  const renderSelectedProducts = () => (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 bg-white flex justify-between items-center">
        <h4 className="text-sm font-medium text-gray-900">Produits sélectionnés ({selectedProducts.length})</h4>
        {/* Only show back button on mobile view */}
        <button
          className="md:hidden rounded-full p-1.5 hover:bg-gray-100"
          onClick={toggleMobileCart}
        >
          <FaArrowLeft className="h-4 w-4 text-gray-600" />
        </button>
      </div>
      
      <div className="p-4 overflow-y-auto flex-1">
        {selectedProducts.length > 0 ? (
          <div className="space-y-3">
            {selectedProducts.map((product) => (
              <div key={product.id} className="flex bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                <div className="flex-1 min-w-0">
                  <h5 className="text-sm font-medium text-gray-900 truncate">{product.name}</h5>
                  <p className="mt-1 text-xs text-gray-500">{product.price.toFixed(2)} TND</p>
                </div>
                <div className="flex items-center ml-4">
                  <button
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      updateQuantity(product.id, Math.max(1, product.quantity - 1));
                    }}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <input
                    type="number"
                    className="mx-2 w-12 text-center border-gray-300 rounded-md text-sm focus:ring-[#174540] focus:border-[#174540]"
                    value={product.quantity}
                    onChange={(e) => {
                      const newQuantity = parseInt(e.target.value) || 1;
                      updateQuantity(product.id, newQuantity);
                    }}
                    min="1"
                    max={allowOutOfStock ? "999" : product.stock}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      // If allowOutOfStock, just increment without limit check
                      const newQuantity = allowOutOfStock 
                        ? product.quantity + 1 
                        : Math.min(product.stock, product.quantity + 1);
                      updateQuantity(product.id, newQuantity);
                    }}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  <button
                    className="ml-2 text-red-400 hover:text-red-600 focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromSelection(product.id);
                    }}
                  >
                    <FaTimes className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <svg className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2H5a2 2 0 00-2 2v2M7 7h10" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">Aucun produit sélectionné</p>
            <p className="mt-1 text-xs text-gray-400">Cliquez sur un produit pour le sélectionner</p>
          </div>
        )}
      </div>
      
      {/* Add a continue button on mobile view */}
      <div className="p-4 border-t border-gray-200 md:hidden">
        <button
          type="button"
          className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none"
          onClick={toggleMobileCart}
        >
          Continuer
        </button>
      </div>
    </div>
  );
  
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="bg-white py-4 px-6 border-b border-gray-200 flex justify-between items-center">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center"
                  >
                    <div className="p-2 rounded-md bg-[#f5f8f7] mr-3">
                      <svg className="h-5 w-5 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <span className="hidden sm:inline">Sélectionner des produits</span>
                    <span className="sm:hidden">Produits</span>
                    <span className="ml-2">({totalProducts})</span>
                  </Dialog.Title>
                  
                  {/* Cart toggle button on mobile */}
                  <div className="flex items-center">
                    <button
                      type="button"
                      className="md:hidden mr-2 relative p-2 rounded-full text-gray-400 hover:bg-gray-100"
                      onClick={toggleMobileCart}
                    >
                      <FaListUl className="h-5 w-5" />
                      {selectedProducts.length > 0 && (
                        <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-[#174540] rounded-full">
                          {selectedProducts.length}
                        </span>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={onClose}
                    >
                      <FaTimes className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                
                {/* Search and filter section - show only in main view */}
                {!selectedProductForVariants && !showCartOnMobile && (
                  <div className="border-b border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
                    <div className="flex flex-col space-y-4">
                      {/* Search with filter button row */}
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaSearch className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            className="pl-10 py-2 block w-full shadow-sm border-gray-300 rounded-md focus:ring-[#174540] focus:border-[#174540]"
                            placeholder="Rechercher un produit..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                // Trigger search on Enter key
                                handleApplyFilters();
                              }
                            }}
                          />
                          {searchTerm && (
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                              <button
                                onClick={() => setSearchTerm('')}
                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                type="button"
                              >
                                <FaTimes className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Filter and Clear buttons */}
                        <div className="flex space-x-2">
                          {/* Filter button */}
                          <button
                            type="button"
                            onClick={handleApplyFilters}
                            className="flex-shrink-0 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                          >
                            <FaFilter className="h-4 w-4 mr-2" />
                            Filtrer
                          </button>
                          
                          {/* Clear button - only show if there are active filters */}
                          {(searchTerm || stockFilter !== 'all') && (
                            <button
                              type="button"
                              onClick={handleClearFilters}
                              className="flex-shrink-0 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Effacer
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Stock filter row */}
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center space-x-2 mr-3">
                          <FaBoxOpen className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-500">Disponibilité:</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => {
                              setStockFilter('all');
                              setPage(1);
                              setFilterApplied(prev => !prev);
                            }}
                            className={`px-3 py-1.5 text-sm rounded flex items-center ${
                              stockFilter === 'all'
                                ? 'bg-[#174540] text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <span>Tous</span>
                          </button>
                          <button
                            onClick={() => {
                              setStockFilter('inStock');
                              setPage(1);
                              setFilterApplied(prev => !prev);
                            }}
                            className={`px-3 py-1.5 text-sm rounded flex items-center ${
                              stockFilter === 'inStock'
                                ? 'bg-green-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <FaBox className="h-3 w-3 mr-1.5" />
                            <span>En stock</span>
                          </button>
                          <button
                            onClick={() => {
                              setStockFilter('outOfStock');
                              setPage(1);
                              setFilterApplied(prev => !prev);
                            }}
                            className={`px-3 py-1.5 text-sm rounded flex items-center ${
                              stockFilter === 'outOfStock'
                                ? 'bg-red-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <FaBox className="h-3 w-3 mr-1.5" />
                            <span>Rupture</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Main content area - show products or cart based on mobile/desktop view */}
                <div className="flex flex-col md:flex-row h-[500px] md:h-[60vh] max-h-[600px]">
                  {/* Show cart on mobile if toggled, otherwise show products/variants */}
                  {showCartOnMobile ? (
                    // Mobile cart view
                    <div className="w-full md:hidden overflow-y-auto">
                      {renderSelectedProducts()}
                    </div>
                  ) : (
                    // Products or Variants listing
                    <div className="w-full md:w-2/3 overflow-y-auto p-4">
                      {loading && page === 1 ? (
                        <div className="flex items-center justify-center h-full">
                          <FaSpinner className="h-8 w-8 text-[#174540] animate-spin" />
                        </div>
                      ) : (
                        selectedProductForVariants ? renderVariantSelectionView() : renderProductList()
                      )}
                    </div>
                  )}
                  
                  {/* Selected products - only visible on desktop */}
                  <div className="hidden md:block md:w-1/3 overflow-y-auto border-l border-gray-200 bg-gray-50">
                    {renderSelectedProducts()}
                  </div>
                </div>
                
                {/* Footer with action buttons */}
                <div className="bg-gray-50 px-4 sm:px-6 py-4 flex flex-col sm:flex-row justify-between items-center border-t border-gray-200 gap-3">
                  <div className="text-sm text-gray-500 w-full sm:w-auto">
                    {selectedProducts.length} produit(s) sélectionné(s)
                  </div>
                  <div className="flex w-full sm:w-auto space-x-3">
                    <button
                      type="button"
                      className="w-1/2 sm:w-auto py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                      onClick={onClose}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="w-1/2 sm:w-auto py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleConfirmSelection}
                      disabled={selectedProducts.length === 0}
                    >
                      <span className="hidden sm:inline">Ajouter les produits</span>
                      <span className="sm:hidden">Ajouter</span>
                      <span> ({selectedProducts.length})</span>
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ProductSelectionModal;